import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StringHelper } from '../../../core/helpers/strings.helper';
import { MobileDelegateService } from '../../../core/services/mobile-delegate.service';
import { MobileWebViewService } from '../../../core/services/mobile-web-view.service';
import { ScreenCaptureService } from '../../../core/services/screen-capture.service';
import { WclService } from '../../../core/wcl/services/wcl-service.service';
import { DelegateType } from '../../../shared/models/utility.model';
import { DEFAULT_MODAL_KAMONA_CLASSES, DEFAULT_MODAL_WIDTH, MONIEPOINT_DESIGN_TYPE } from '../../../shared/constants';
import { ModalOptionsConfig, ModalService } from '../nz-modal-service/modal.service';
import { WlcCameralModalComponent } from './modal/wlc-cameral-modal/wlc-cameral-modal.component';

class ImageSnippet {
	constructor(
		public src: string,
		public file: File,
		public fileName: string,
		public fileSize: string,
		public imageLabel: string,
		public lat: number,
		public long: number
	) { }
}

@Component({
	selector: 'app-working-capital-upload-input',
	templateUrl: './working-capital-upload-input.component.html',
	styleUrls: ['./working-capital-upload-input.component.scss'],
})
export class WorkingCapitalUploadInputComponent implements OnInit {
	selectedFile: ImageSnippet;
	showFile = false;
	@Input() imageLabel: string;
	@Output() onDeleteFile: EventEmitter<any> = new EventEmitter();
	@Output() onSaveImage: EventEmitter<any> = new EventEmitter();
	showCamera = false;
	runningModal: any;
	lat: number;
	long: number;
	isMobileUser: any;
	loading: boolean;
	readonly CHARSET_FOR_CODE_GENERATION = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	apiLoader: boolean;
	fileInfoWithReference: any;


	constructor(
		public modal: ModalService,
		private delegateService: MobileDelegateService,
		public mobileService: MobileWebViewService,
		private modalService: ModalService,
		public WCLService: WclService,
		private screenCaptureService: ScreenCaptureService
	) { }

	ngOnInit(): void {
		this.isMobileUser = this.mobileService.isMobileWebViewUser();
	}

	processFile(imageInput: any) {
		const file: File = imageInput.files[0];
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.addEventListener('load', (event: any) => {
			this.saveImageSnippet(file, event);
		});
		this.showFile = true;
	}

	saveImageSnippet(file: File, event?: any) {
		let fileSize: any = file.size;
		let fileExtensions = ['Bytes', 'KB', 'MB', 'GB'],
			i = 0;
		while (fileSize > 900) {
			fileSize /= 1024;
			i++;
		}
		fileSize = Math.round(fileSize) + ' ' + fileExtensions[i];

		this.selectedFile = new ImageSnippet(
			event?.target?.result || event?.imageAsDataUrl || event || '',
			file,
			file.name,
			fileSize,
			this.imageLabel,
			this.lat,
			this.long
		);

		this.saveImageToFileService(this.selectedFile);
	}

	removeFile() {
		this.showFile = false;
		this.onDeleteFile.emit(this.fileInfoWithReference);
		this.selectedFile = null;
	}

	showCAM() {
		if (this.isMobileUser){
			this.getCameraEnabledDelegate();
		} else {
			this.loading = true;
			this.getLocationWeb((res) => {
				this.launchCamera();
				this.loading = false;
			}, (err) => {
				this.loading = false;
				this.launchAlertModal(err);
			});
		}
	}

	getCameraEnabledDelegate() {
		if (this.isMobileUser) {
			try {
				this.delegateService
					.getDelegateData(DelegateType.isCameraEnabled)
					.then((res) => {
						if (res === true) {
							this.getLocationDelegateAndLaunchCameraMobile();
						} else {
							this.requestCameraPermissionDelegate();
						}
					})
					.catch((error) => {
						this.launchAlertModal(error);
						this.loading = false;
					});
			} catch (error) {
				this.launchAlertModal(error);
				this.loading = false;
			}
		}
	}

	requestCameraPermissionDelegate() {
		try {
			this.delegateService
				.getDelegateData(DelegateType.requestCameraPermission)
				.then((res) => {
					if (res === true) {
						this.getLocationDelegateAndLaunchCameraMobile();
					}
				})
				.catch((error) => {
					this.launchAlertModal(error);
					this.loading = false;
				});
		} catch (error) {
			this.loading = false;
			this.launchAlertModal(error);
		}
	}

	launchCameraDelegate() {
		this.loading = true;
		try {
			this.delegateService
				.getDelegateData(DelegateType.launchCamera)
				.then((res) => {
					if (!res) {
						this.launchAlertModal('unable to get camera picture info');
						return;
					}

					const fileObj = JSON.parse(res);

					if (fileObj && Object.keys(fileObj).length === 0) {
						this.launchAlertModal('no image information obtained');
						return;
					}

					const encodedFile = `data:${fileObj?.contentType};base64,${fileObj?.encodedFile}`;
					const file = this.screenCaptureService.dataURLtoFile(encodedFile, this.generateCode(6) || fileObj?.fileName);

					const imageinBase64: any = encodedFile;

					this.getLocationMobile((resOnSuccess: any) => {
						this.lat = resOnSuccess?.lat;
						this.long = resOnSuccess?.long;
						this.saveImageSnippet(file, imageinBase64);
						this.loading = false;
					}, (err) => {
						this.loading = false;
						this.launchAlertModal(err?.message || err);
					});
				}).catch((error) => {
					this.launchAlertModal(error);
				}).finally(() => {
					this.loading = false;
				});
		} catch (error) {
			this.launchAlertModal(error);
		} finally {
			this.loading = false;
		}
	}

	getLocationDelegateAndLaunchCameraMobile() {
		this.loading = true;
		this.getLocationMobile((res: any) => {
			this.loading = false;
			this.launchCameraDelegate();
		}, (error: any) => {
			this.loading = false;
			this.launchAlertModal(error);
		});
	}

	getLocationMobile(onSuccess?: (res: any) => void, onError?: (err: any) => void) {
		try {
			this.delegateService
				.getDelegateData(DelegateType.getUserLocation)
				.then((res) => {
					if (!res) {
						this.launchAlertModal('unable to get location info');
						return;
					}
					const response = JSON.parse(res);

					if (response?.success && !response?.failed) {
						if (onSuccess && typeof onSuccess === 'function') {
							onSuccess(response?.success);
						}
					} else if (response?.failed && !response?.success) {
						if (onError && typeof onError === 'function') {
							onError(response?.failed);
						}
					}
				})
				.catch((error) => {
					if (onError && typeof onError === 'function') {
						onError(error);
					}
					this.loading = false;
				});
		} catch (error) {
			this.loading = false;
			if (onError && typeof onError === 'function') {
				onError(error);
			}
		}
	}

	getLocationWeb(onSuccess?: (res: any) => void, onError?: (err: any) => void) {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition((position: any) => {
				if (position) {
					this.lat = position.coords.latitude;
					this.long = position.coords.longitude;
					if (onSuccess && typeof onSuccess === 'function') {
						onSuccess({ lat: this.lat, long: this.long });
					}
				}
			}, (error: any) => {
				this.loading = false;
				if (onError && typeof onError === 'function') {
					onError('please grant location access on your browser.');
				}
			});
		} else {
			this.loading = false;
			if (onError && typeof onError === 'function') {
				onError('Geolocation is not supported by this browser.');
			}
		}
	}

	launchCamera() {
		this.showCamera = !this.showCamera;
		this.launchModal(WlcCameralModalComponent);
	}

	createModalConfig() {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.callback = (event?: any) => {
			const file: File = event.files[0];
			const snapshot: any = event.snapshots[0];

			this.getLocationWeb((res) => {
				this.lat = res?.lat;
				this.long = res?.long;
				this.saveImageSnippet(file, snapshot);
			}, (error) => {
				this.launchAlertModal(error);
			});

			this.runningModal.destroy();
		};

		config.nzClosable = true;
		return config;
	}

	launchModal(component: any) {
		const config = this.createModalConfig();
		this.runningModal = this.modal.createModal(component, config);
	}

	launchAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = 'Request failed';
		config.className = DEFAULT_MODAL_KAMONA_CLASSES;
		config.nzWidth = DEFAULT_MODAL_WIDTH;
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.callbackName = params?.callbackName || 'Close';
		config.nzComponentParams.message = params;
		this.runningModal = this.modalService.alert(config);
	}

	generateCode(length: number) {
		const charset = this.CHARSET_FOR_CODE_GENERATION;
		let retVal = '';
		for (let i = 0, n = charset.length; i < length; ++i) {
			retVal += charset.charAt(Math.floor(Math.random() * n)).toUpperCase();
		}
		return 'MP-IMG' + retVal;
	}

	saveImageToFileService($event: any) {
		this.apiLoader = true;
		this.WCLService.uploadFile($event.file).then((res) => {
			const fileInfo = {
				ref: res?.fileReference,
				label: $event.imageLabel,
				latitude: $event.lat,
				longitude: $event.long
			};
			this.fileInfoWithReference = fileInfo;
			this.onSaveImage.emit(fileInfo);
			this.showFile = true;
		}).catch((err) => {
			this.removeFile();
			this.launchAlertModal(err?.error?.message || StringHelper.getErrorMessageHttpErrorResponse(err));
		}).finally(() => {
			this.apiLoader = false;
		});
	}
}

